import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link2, Send, Inbox, Clock, CheckCircle2 } from "lucide-react";
import { vitaeApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function VitaePublica() {
  const { projectId } = useParams();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  // Manual VITAE-PUBLICA data receive (SRV, V1, V7)
  const [rcv, setRcv] = useState({ SRV: "", V1: "", V7: "" });

  const load = () => vitaeApi.history(projectId).then(setHistory);
  useEffect(load, [projectId]);

  const sendSync = async () => {
    setLoading(true);
    try {
      const r = await vitaeApi.sync(projectId);
      toast.success(`Payload envoyé (local) — TAVD ${r.payload_sent.tavd?.toFixed?.(1) ?? "—"}%`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const submitReceive = async (e) => {
    e.preventDefault();
    const payload = {
      SRV: rcv.SRV ? Number(rcv.SRV) : null,
      V1: rcv.V1 ? Number(rcv.V1) : null,
      V7: rcv.V7 ? Number(rcv.V7) : null,
    };
    await vitaeApi.receive(projectId, payload);
    toast.success("Données VITAE-PUBLICA enregistrées");
    setRcv({ SRV: "", V1: "", V7: "" });
    load();
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto w-full" data-testid="vitae-page">
      <div className="label-mono text-[10px] text-muted-foreground">INTÉGRATION</div>
      <h1 className="mt-1 font-serif font-bold text-4xl flex items-center gap-3">
        <Link2 className="text-gold" /> VITAE-PUBLICA
      </h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Pont bidirectionnel avec l'application <strong>VITAE-PUBLICA</strong> (pendant vitaliste du prélèvement).
        DEBT-ANALYZER PRO envoie les indicateurs de dette (D1, D2, D5, V5, V6) et peut recevoir en retour les métriques
        du retour vitaliste (SRV, V1, V7).
      </p>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {/* Envoi */}
        <div className="border border-border bg-card rounded-sm p-6" data-testid="vitae-send-card">
          <div className="flex items-center gap-2">
            <Send size={18} className="text-marine dark:text-gold" />
            <h2 className="font-serif font-bold">Envoi vers VITAE-PUBLICA</h2>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Les indicateurs vitalistes calculés sont packagés et enregistrés. Mode autonome
            activé — le payload est stocké localement, prêt à l'export.
          </p>
          <Button onClick={sendSync} disabled={loading}
                  className="mt-5 w-full bg-marine hover:bg-marine/90 text-white" data-testid="vitae-sync-btn">
            {loading ? "Envoi…" : "Synchroniser maintenant"}
          </Button>
        </div>

        {/* Réception manuelle */}
        <form onSubmit={submitReceive} className="border border-border bg-card rounded-sm p-6"
              data-testid="vitae-receive-card">
          <div className="flex items-center gap-2">
            <Inbox size={18} className="text-marine dark:text-gold" />
            <h2 className="font-serif font-bold">Réception depuis VITAE-PUBLICA</h2>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Saisissez manuellement les métriques renvoyées par VITAE-PUBLICA pour enrichir le contexte.
          </p>
          <div className="mt-5 space-y-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">SRV — Score Retour Vitaliste</Label>
              <Input type="number" step="0.01" value={rcv.SRV} onChange={(e) => setRcv((s) => ({ ...s, SRV: e.target.value }))}
                     className="h-9 font-mono mt-1" data-testid="vitae-srv-input" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">V1 — Jours de vie fiscale</Label>
              <Input type="number" step="0.01" value={rcv.V1} onChange={(e) => setRcv((s) => ({ ...s, V1: e.target.value }))}
                     className="h-9 font-mono mt-1" data-testid="vitae-v1-input" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">V7 — Déficit vitaliste individuel</Label>
              <Input type="number" step="0.01" value={rcv.V7} onChange={(e) => setRcv((s) => ({ ...s, V7: e.target.value }))}
                     className="h-9 font-mono mt-1" data-testid="vitae-v7-input" />
            </div>
          </div>
          <Button type="submit" variant="outline" className="mt-5 w-full" data-testid="vitae-receive-btn">
            Enregistrer les données reçues
          </Button>
        </form>
      </div>

      {/* Historique */}
      <div className="mt-10">
        <h2 className="font-serif font-bold text-2xl">Historique des synchronisations ({history.length})</h2>
        {history.length === 0 ? (
          <div className="mt-4 border border-dashed border-border rounded-sm p-8 text-center text-sm text-muted-foreground">
            Aucune synchronisation encore. Lancez-en une ci-dessus.
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="vitae-history">
            {history.map((h) => (
              <div key={h.id} className="border border-border bg-card rounded-sm p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs">
                    {h.sync_status === "received" ? <CheckCircle2 size={14} className="text-success" /> : <Clock size={14} className="text-gold" />}
                    <span className="label-mono text-[10px] uppercase">{h.sync_status}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString("fr-FR")}</span>
                </div>
                {h.payload_sent && (
                  <pre className="mt-3 text-[10px] font-mono bg-muted/30 p-3 rounded-sm overflow-x-auto">
                    {JSON.stringify(h.payload_sent, null, 2)}
                  </pre>
                )}
                {h.payload_received && (
                  <pre className="mt-3 text-[10px] font-mono bg-gold/5 border border-gold/30 p-3 rounded-sm overflow-x-auto">
                    ← {JSON.stringify(h.payload_received, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
